import { Button } from '@/components/ui/shadcn/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/shadcn/table';
import { TableColumnHeader } from '@/components/ui/table-cells/TableColumnHeader';
import { useTranslations } from '@/hooks/useTranslations';
import { cn } from '@/lib/utils';
import type { MppFileSummary, MppFilterOption, MppStatus } from '@/types/mpp';
import { Link, router } from '@inertiajs/react';
import { Download, RefreshCw, Trash2 } from 'lucide-react';
import { route } from 'ziggy-js';

interface MppFileTableProps {
    files: MppFileSummary[];
    statuses: MppFilterOption[];
    users: MppFilterOption[];
    sortColumn: string | null;
    sortDirection: 'asc' | 'desc' | null;
    onSortChange: (column: string, direction: 'asc' | 'desc') => void;
    columnFilters: Record<string, string[]>;
    onFilterChange: (column: string, values: string[]) => void;
    canDelete: boolean;
    onRefresh: (file: MppFileSummary) => void;
    onDelete: (file: MppFileSummary) => void;
    emptyMessage: string;
}

const STATUS_CLASSES: Record<MppStatus, string> = {
    queued: 'bg-info-subtle text-info-subtle-foreground',
    processing: 'bg-warning-subtle text-warning-subtle-foreground',
    completed: 'bg-success-subtle text-success-subtle-foreground',
    failed: 'bg-danger-subtle text-danger-subtle-foreground',
};

function formatBytes(bytes: number | null): string {
    if (!bytes || bytes <= 0) {
        return '—';
    }
    const units = ['B', 'KB', 'MB', 'GB'];
    const exponent = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
    const value = bytes / Math.pow(1024, exponent);
    return `${value.toFixed(value >= 10 || exponent === 0 ? 0 : 1)} ${units[exponent]}`;
}

function formatDate(iso: string | null): string {
    if (!iso) {
        return '—';
    }
    const date = new Date(iso);
    return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString();
}

function daysUntil(iso: string | null): number | null {
    if (!iso) {
        return null;
    }
    const target = new Date(iso);
    if (Number.isNaN(target.getTime())) {
        return null;
    }
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    target.setHours(0, 0, 0, 0);
    return Math.round((target.getTime() - start.getTime()) / 86_400_000);
}

export function MppFileTable({
    files,
    statuses,
    users,
    sortColumn,
    sortDirection,
    onSortChange,
    columnFilters,
    onFilterChange,
    canDelete,
    onRefresh,
    onDelete,
    emptyMessage,
}: MppFileTableProps) {
    const { t } = useTranslations();

    const columns: Array<{ id: string; label: string; sortable: boolean; filterKey?: 'status' | 'uploaded_by'; align?: 'right' }> = [
        { id: 'original_name', label: t('mpp.index.table.name', 'File'), sortable: true },
        { id: 'format', label: t('mpp.index.table.format', 'Format'), sortable: true },
        { id: 'status', label: t('mpp.index.table.status', 'Status'), sortable: true, filterKey: 'status' },
        { id: 'tasks', label: t('mpp.index.table.tasks', 'Tasks'), sortable: false, align: 'right' },
        { id: 'uploaded_by', label: t('mpp.index.table.uploaded_by', 'Uploaded by'), sortable: false, filterKey: 'uploaded_by' },
        { id: 'expires_at', label: t('mpp.index.table.expires', 'Retention'), sortable: true },
        { id: 'actions', label: t('mpp.index.table.actions', 'Actions'), sortable: false, align: 'right' },
    ];

    const filterOptionsFor = (filterKey?: 'status' | 'uploaded_by'): MppFilterOption[] => {
        if (filterKey === 'status') {
            return statuses;
        }
        if (filterKey === 'uploaded_by') {
            return users;
        }
        return [];
    };

    const renderRetention = (file: MppFileSummary) => {
        const days = daysUntil(file.expires_at);
        if (days === null) {
            return <span className="text-muted-foreground text-sm">—</span>;
        }
        if (days < 0) {
            return <span className="text-danger-subtle-foreground text-sm font-medium">{t('mpp.index.retention.expired', 'Expired')}</span>;
        }
        if (days === 0) {
            return (
                <span className="text-warning-subtle-foreground text-sm font-medium">{t('mpp.index.retention.expires_today', 'Expires today')}</span>
            );
        }
        const label = t('mpp.index.retention.expires_in', 'Expires in :days d').replace(':days', String(days));
        return <span className={cn('text-sm', days <= 3 ? 'text-warning-subtle-foreground font-medium' : 'text-muted-foreground')}>{label}</span>;
    };

    return (
        <div className="bg-card mb-4 overflow-x-auto rounded-xl border px-4 py-2.5">
            <Table>
                <TableHeader>
                    <TableRow>
                        {columns.map((column) => {
                            // Columns with neither sort nor filter render a plain label (no empty menu).
                            if (!column.sortable && !column.filterKey) {
                                return (
                                    <TableHead key={column.id} className={cn('px-3', column.align === 'right' && 'text-right')}>
                                        {column.label}
                                    </TableHead>
                                );
                            }
                            const selectedFilters = column.filterKey ? columnFilters[column.filterKey] || [] : [];
                            return (
                                <TableHead key={column.id} className={cn(column.align === 'right' && 'text-right')}>
                                    <TableColumnHeader
                                        label={column.label}
                                        sortDirection={sortColumn === column.id ? sortDirection : null}
                                        onSortAscending={column.sortable ? () => onSortChange(column.id, 'asc') : undefined}
                                        onSortDescending={column.sortable ? () => onSortChange(column.id, 'desc') : undefined}
                                        filterOptions={filterOptionsFor(column.filterKey)}
                                        selectedFilters={selectedFilters}
                                        onFilterChange={column.filterKey ? (values) => onFilterChange(column.filterKey!, values) : undefined}
                                        isFiltered={selectedFilters.length > 0}
                                    />
                                </TableHead>
                            );
                        })}
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {files.length === 0 ? (
                        <TableRow>
                            <TableCell colSpan={columns.length} className="text-muted-foreground py-8 text-center">
                                {emptyMessage}
                            </TableCell>
                        </TableRow>
                    ) : (
                        files.map((file) => (
                            <TableRow
                                key={file.id}
                                className="hover:bg-muted/50 cursor-pointer transition-colors"
                                onClick={() => router.visit(route('mpp.show', file.id))}
                            >
                                <TableCell>
                                    <Link
                                        href={route('mpp.show', file.id)}
                                        onClick={(event) => event.stopPropagation()}
                                        className="font-semibold hover:underline"
                                    >
                                        {file.original_name}
                                    </Link>
                                    <div className="text-muted-foreground text-xs">{formatBytes(file.file_size)}</div>
                                </TableCell>
                                <TableCell className="text-sm uppercase">{file.format ?? '—'}</TableCell>
                                <TableCell>
                                    <span
                                        className={cn(
                                            'inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium',
                                            STATUS_CLASSES[file.status],
                                        )}
                                        title={file.status === 'failed' ? (file.error ?? undefined) : undefined}
                                    >
                                        {t(`mpp.status.${file.status}`, file.status)}
                                    </span>
                                </TableCell>
                                <TableCell className="text-right text-sm tabular-nums">{file.project_meta?.task_count ?? '—'}</TableCell>
                                <TableCell className="text-muted-foreground text-sm">{file.uploaded_by ?? '—'}</TableCell>
                                <TableCell>
                                    {renderRetention(file)}
                                    <div className="text-muted-foreground text-xs">{formatDate(file.created_at)}</div>
                                </TableCell>
                                <TableCell onClick={(event) => event.stopPropagation()}>
                                    <div className="flex items-center justify-end gap-1">
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            aria-label={t('mpp.index.actions.refresh', 'Keep 30 more days')}
                                            title={t('mpp.index.actions.refresh', 'Keep 30 more days')}
                                            onClick={() => onRefresh(file)}
                                        >
                                            <RefreshCw />
                                        </Button>
                                        <Button asChild variant="ghost" size="icon" aria-label={t('mpp.index.actions.download', 'Download')}>
                                            <a href={route('mpp.download', file.id)}>
                                                <Download />
                                            </a>
                                        </Button>
                                        {canDelete ? (
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                aria-label={t('mpp.index.actions.delete', 'Delete')}
                                                onClick={() => onDelete(file)}
                                            >
                                                <Trash2 className="text-danger-subtle-foreground" />
                                            </Button>
                                        ) : null}
                                    </div>
                                </TableCell>
                            </TableRow>
                        ))
                    )}
                </TableBody>
            </Table>
        </div>
    );
}
